/**
 * KnowledgeBase Page
 * Standalone page for managing Knowledge Base files (replaces drawer)
 *
 * Features:
 * - Multi-file upload
 * - Quota tracking
 * - File list with delete
 * - Responsive layout
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CloudUploadIcon } from "lucide-react";
import { Alert, AlertAction, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/useAuth";
import { useKnowledgeBase } from "@/features/knowledge-base/useKnowledgeBase";
import { QuotaIndicator } from "@/features/knowledge-base/QuotaIndicator";
import { FileList } from "@/features/knowledge-base/FileList";
import { DeleteConfirmDialog } from "@/features/knowledge-base/DeleteConfirmDialog";
import { Page } from "@/components/layout/Page";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  MultiFileUpload,
  type FileValidationOptions,
} from "@/features/upload/components/MultiFileUpload";

const KB_VALIDATION: FileValidationOptions = {
  allowedTypes: [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
    "text/markdown",
  ],
  maxFileSize: 100 * 1024 * 1024, // 100MB
  accept: ".pdf,.docx,.txt,.md",
};

export default function KnowledgeBase() {
  const { t } = useTranslation(["kb", "common", "auth"]);
  const { user, loading: authLoading } = useAuth();
  const {
    files,
    quota,
    uploadFiles,
    deleteFile,
    isLoading,
    error,
    isUploading,
    uploadProgress,
    refetch,
  } = useKnowledgeBase({ t });

  const [deleteTarget, setDeleteTarget] = useState<
    { id: string; filename: string } | null
  >(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleFilesSelect = (files: File[]) => {
    setSelectedFiles(files);
    setUploadError(null);
  };

  const handleUploadError = (error: string) => {
    setUploadError(error);
  };

  const handleUploadClick = async () => {
    if (selectedFiles.length === 0) return;

    setUploadError(null);

    try {
      await uploadFiles(selectedFiles);
      setSelectedFiles([]);
    } catch (err) {
      const message = err instanceof Error && err.message
        ? err.message
        : t("kb:upload.errors.uploadFailed");
      setUploadError(message);
    }
  };

  const handleDelete = (fileId: string, filename: string) => {
    setDeleteTarget({ id: fileId, filename });
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteFile(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      const message = err instanceof Error && err.message
        ? err.message
        : t("kb:deleteDialog.failed");
      setDeleteError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
    setDeleteError(null);
  };

  // Loading state
  if (authLoading) {
    return (
      <Page max={900}>
        <div className="rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--card)] p-8 text-center">
          <p className="text-sm text-[var(--muted-foreground)]">
            {t("auth:login.checkingSession")}
          </p>
        </div>
      </Page>
    );
  }

  // Not authenticated
  if (!user) {
    return (
      <Page max={900}>
        <PageHeader
          eyebrow={t("kb:badge")}
          sub={t("kb:page.subtitle")}
          title={t("kb:page.title")}
        />

        <div className="rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--card)] p-8">
          <h2 className="mb-3 text-xl font-semibold text-[var(--foreground)]">
            {t("kb:page.signInRequired")}
          </h2>
          <p className="mb-6 text-sm text-[var(--muted-foreground)]">
            {t("kb:page.signInMessage")}
          </p>
          <Button
            className="w-full"
            render={<Link to="/login" />}
            variant="cta"
          >
            {t("common:nav.signIn")}
          </Button>
        </div>
      </Page>
    );
  }

  return (
    <Page max={900}>
      <PageHeader
        eyebrow={t("kb:badge")}
        sub={t("kb:page.subtitle")}
        title={t("kb:page.title")}
      />

      {/* Loading state */}
      {isLoading && (
        <div
          aria-label={t("kb:page.loading")}
          className="animate-pulse space-y-6"
          data-test="kb-loading"
          role="status"
        >
          <div className="h-32 rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--secondary)]" />
          <div className="h-48 rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--secondary)]" />
          <div className="h-40 rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--secondary)]" />
        </div>
      )}

      {/* Error message */}
      {error && (
        <Alert className="mb-6" data-test="kb-error" variant="error">
          <AlertDescription className="flex-1">{error}</AlertDescription>
          <AlertAction>
            <Button
              data-test="kb-retry"
              onClick={() => void refetch()}
              size="sm"
              variant="outline"
            >
              {t("common:buttons.retry")}
            </Button>
          </AlertAction>
        </Alert>
      )}

        {/* Main content */}
        {!isLoading && quota && (
          <div className="space-y-6">
            {/* Quota Card */}
            <div className="rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--card)] p-6">
              <h2 className="mb-4 text-lg font-semibold text-[var(--foreground)]">
                {t("kb:quota.title")}
              </h2>
              <QuotaIndicator quota={quota} />
            </div>

            {/* Upload Card */}
            <div className="rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--card)] p-6">
              <h2 className="mb-4 text-lg font-semibold text-[var(--foreground)]">
                {t("kb:page.uploadSection")}
              </h2>

              <MultiFileUpload
                onFilesSelect={handleFilesSelect}
                onError={handleUploadError}
                disabled={quota.fileCount >= quota.maxFiles ||
                  quota.totalSize >= quota.maxSize || isUploading}
                validationOptions={KB_VALIDATION}
                translationNamespace="kb"
              />

              {uploadError && (
                <Alert className="mt-4" data-test="kb-upload-error" variant="error">
                  <AlertDescription>{uploadError}</AlertDescription>
                </Alert>
              )}

              {isUploading && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[var(--foreground)]">
                      {t("kb:upload.uploading")}
                    </span>
                    <span className="text-sm text-[var(--muted-foreground)]">
                      {uploadProgress}%
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--secondary)]">
                    <div
                      className="h-full w-full origin-left rounded-full bg-primary transition-transform duration-[var(--dur-base)] ease-[var(--ease-out)]"
                      style={{ transform: `scaleX(${uploadProgress / 100})` }}
                    />
                  </div>
                </div>
              )}

              <Button
                variant="cta"
                size="lg"
                onClick={handleUploadClick}
                disabled={selectedFiles.length === 0 || isUploading ||
                  quota.fileCount >= quota.maxFiles ||
                  quota.totalSize >= quota.maxSize}
                data-test="kb-upload-submit"
                className="mt-4 w-full"
                loading={isUploading}
              >
                <CloudUploadIcon aria-hidden="true" className="size-5" />
                <span>
                  {isUploading
                    ? t("kb:upload.uploading")
                    : selectedFiles.length > 0
                    ? t("kb:upload.uploadButton", {
                      count: selectedFiles.length,
                    })
                    : t("kb:upload.uploadFiles")}
                </span>
              </Button>
            </div>

            {/* File List Card */}
            <div className="rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--card)] p-6">
              <h2 className="mb-4 text-lg font-semibold text-[var(--foreground)]">
                {t("kb:fileList.title")}
              </h2>
              <FileList files={files} onDelete={handleDelete} />
            </div>
          </div>
        )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        filename={deleteTarget?.filename || null}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        error={deleteError}
        isDeleting={isDeleting}
      />
    </Page>
  );
}
